import { NotificationResponseNext } from "@alme/types/response/NotificationResponse";
import { AlmeEnvironmentConfig } from "./AlmeEnvironmentConfig";
import { AlmeSessionData } from "@alme/session-ts/AlmeSessionData";
import { AlmeAppState } from "../components/AlmeApp/AlmeAppState";
import { createIntlCache, createIntl } from "react-intl";
import { getCurrentIntlConfig } from "../utils/PersonaUtils";


function getSessionExpiredResponse(entryId: string): NotificationResponseNext {
    const currentIntlConfig = getCurrentIntlConfig();
    const cache = createIntlCache();
    const intl = createIntl({ locale: currentIntlConfig.locale, messages: currentIntlConfig.messages }, cache);

    return {
        id: "",
        userId: "",
        responseRevision: 0,
        maskedInput: "",
        text: intl.formatMessage({
            id: "sessionUtils.timeoutMessage",
            defaultMessage: 'Default session timeout message',
            description: 'AlmeSessionUtils session timeout message',
        }),
        navUrl: null,
        responseActions: [],
        displayLinkCollection: null,
        deferredAppCall: null,
        isReverted: false,
        extensionData: {
            MessageIndex: 0,
            entryId,
            isSessionTimeoutResponse: true,
        },
    };
}


export class AlmeSessionUtils {

    private almeSessionData: AlmeSessionData;
    private almeState: AlmeAppState;
    private getAlmeSessionFromStorage: () => AlmeSessionData;
    private clearSessionData: () => void;
    private setAlmeState: (state: AlmeAppState) => void;
    private saveLastActivityTime: (lastActivityTime: string) => void;
    public sessionTimer: NodeJS.Timeout | undefined;


    constructor(
        almeSessionData: AlmeSessionData,
        almeState: AlmeAppState,
        getAlmeSessionFromStorage: () => AlmeSessionData,
        clearSessionData: () => void,
        setAlmeState: (state: AlmeAppState) => void,
        saveLastActivityTime: (lastActivityTime: string) => void,
        sessionTimer: NodeJS.Timeout | undefined = undefined,
    ) {
        this.almeSessionData = almeSessionData;
        this.almeState = almeState;
        this.getAlmeSessionFromStorage = getAlmeSessionFromStorage;
        this.clearSessionData = clearSessionData;
        this.setAlmeState = setAlmeState;
        this.saveLastActivityTime = saveLastActivityTime;
        this.sessionTimer = sessionTimer;
    }

    startSessionTimer(): void {
        const sessionLength = this.minutestoMs(Number(AlmeEnvironmentConfig.getInstance().getConfig().ClientSessionTimeout));

        if (!sessionLength || this.sessionTimer) { return; }

        const remainingTime = this.getRemainingTime(sessionLength);

        this.sessionTimer = setTimeout(() => { this.onSessionTimeout(this.almeState.isOpen, this.almeState.isMinimized); }, remainingTime);
    }

    refreshSessionTimer(): void {
        const sessionLength = this.minutestoMs(Number(AlmeEnvironmentConfig.getInstance().getConfig().ClientSessionTimeout));

        if (!sessionLength) { return; }

        this.saveLastActivityTime(new Date().toISOString());

        if (this.sessionTimer) { clearTimeout(this.sessionTimer); }

        this.sessionTimer = setTimeout(() => { this.onSessionTimeout(this.almeState.isOpen, this.almeState.isMinimized); }, sessionLength);
    }

    setOnSessionTimeout(): void {
        const sessionLength = this.minutestoMs(Number(AlmeEnvironmentConfig.getInstance().getConfig().ClientSessionTimeout));

        if (!sessionLength) { return; }

        const remainingTime = this.getRemainingTime(sessionLength);

        if (this.sessionTimer) { clearTimeout(this.sessionTimer); }

        this.sessionTimer = setTimeout(() => { this.onSessionTimeout(this.almeState.isOpen, this.almeState.isMinimized); }, remainingTime);
    }

    private onSessionTimeout(isOpen?: boolean, isMinimized?: boolean): void {

        const almeSessionData = this.getAlmeSessionFromStorage();

        const sessionLength = this.minutestoMs(Number(AlmeEnvironmentConfig.getInstance().getConfig().ClientSessionTimeout) || 0);
        const currentTime = new Date().getTime();
        const lastActivityTime = new Date(almeSessionData.lastActivityTime ?? (currentTime - sessionLength)).getTime();
        const inactivityTime = currentTime - lastActivityTime;

        if (inactivityTime >= sessionLength) {
            const lastResponse = this.almeState.history.slice(-1)[0];
            const lastEntryId = lastResponse?.extensionData?.entryId as string ?? "";

            this.clearSessionData();
            this.setAlmeState({
                ...this.almeState,
                isMinimized,
                isOpen,
                history: [getSessionExpiredResponse(lastEntryId)],
            });
        }
        else {
            const remainingTime = sessionLength - inactivityTime;
            if (this.sessionTimer) { clearTimeout(this.sessionTimer); }

            this.sessionTimer = setTimeout(() => { this.onSessionTimeout(this.almeState.isOpen, this.almeState.isMinimized); }, remainingTime);
        }
    }

    private minutestoMs(minutes: number): number {
        return minutes * 6e4;
    }

    private getRemainingTime(sessionLength: number): number {
        const currentTime = new Date().getTime();
        const lastActivityTime = new Date(this.almeSessionData.lastActivityTime ?? currentTime).getTime();
        const inactivityTime = currentTime - lastActivityTime;
        return (sessionLength - inactivityTime);
    }
}
